import React from 'react';
import { 
  Users, MapPin, Clock, Info, AlertTriangle, 
  ChevronRight, TrendingUp, Search, Plus, X, Star,
  Map as MapIcon, ShieldCheck, BusFront, 
  Timer, UsersRound, Zap, Sparkles, Filter, 
  Navigation, CheckCircle2, MoreVertical,
  ThumbsDown, BarChart3, TrendingDown,
  History, AlertOctagon, UserX, Moon, Battery, SignalLow,
  BatteryLow, ZapOff, HardDrive, Smartphone, TrainFront, Phone, Shield, Headset
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';

interface SafeHouse {
  id: string;
  name: string;
  type: string;
  distance: string;
  isOpen: boolean;
  trustedBy: number;
  reputationRequired?: number;
  hasCharging?: boolean;
}

interface BusStopReport {
  id: string;
  type?: 'bus' | 'train';
  from: string;
  to: string;
  busStop: string;
  location: string;
  state: string;
  country: string;
  crowdLevel: 'low' | 'medium' | 'high' | 'overcrowded';
  busAvailability: 'frequent' | 'scarce' | 'none';
  securityStatus: 'safe' | 'alert' | 'official-presence';
  lastUpdated: string;
  reporter: string;
  reporterScore: number;
  upvotes: number;
  slangTag?: string;
  verifications?: number;
  isVerifiedByUser?: boolean;
  estimatedFare?: string;
  currentFareValue?: number;
  averageFareValue?: number;
  isFareConfirmed?: boolean;
  isComplained?: boolean;
  fareHistory?: { time: string; price: number }[];
  isSharedRideAvailable?: boolean;
  typicalDuration?: string;
  trafficDensity?: 'clear' | 'moving' | 'heavy' | 'gridlock';
  chatCount?: number;
  sevenDayHistory?: { date: string; avgPrice: number }[];
  conductorSafetyRating?: number;
  conductorReviewCount?: number;
  lastLocationLandmark?: string;
  lastLocationTime?: string;
  isSOSActive?: boolean;
  safeHouses?: SafeHouse[];
  activeBuddyRequests?: number;
  isUserLookingForBuddy?: boolean;
  isSafeWalkActive?: boolean;
  coachCrowding?: { coach: number; level: 'low' | 'medium' | 'high' | 'full'; type?: 'quiet' | 'family' | 'general' }[];
  platformSafetyZones?: { id: string; name: string; type: 'police' | 'emergency-phone' | 'staff' }[];
  lastTrainTime?: string; // ISO string or simple time like "22:30"
}

const DUMMY_BUS_STOPS: BusStopReport[] = [
  {
    id: 'bs1',
    type: 'bus',
    from: 'Berger',
    to: 'Oshodi',
    busStop: 'Berger Bus Stop',
    location: 'Ikeja/Oshodi Expressway',
    state: 'Lagos',
    country: 'Nigeria',
    crowdLevel: 'overcrowded',
    busAvailability: 'scarce',
    securityStatus: 'safe',
    lastUpdated: '2 mins ago',
    reporter: 'Segun A.',
    reporterScore: 1420,
    upvotes: 85,
    slangTag: 'Full Everywhere',
    verifications: 24,
    isVerifiedByUser: false,
    estimatedFare: '₦600',
    currentFareValue: 600,
    averageFareValue: 400,
    isFareConfirmed: false,
    isComplained: false,
    isSharedRideAvailable: true,
    typicalDuration: '45-60 mins',
    trafficDensity: 'heavy',
    chatCount: 12,
    conductorSafetyRating: 4.2,
    conductorReviewCount: 15,
    lastLocationLandmark: 'Iyana-Oworo',
    lastLocationTime: '1 min ago',
    isSOSActive: false,
    activeBuddyRequests: 3,
    safeHouses: [
      { id: 'sh1', name: 'Alhaji’s Pharmacy', type: 'Shop/Safe-zone', distance: '120m', isOpen: true, trustedBy: 142, reputationRequired: 0, hasCharging: true },
      { id: 'sh2', name: 'St. Peter Church', type: 'Worship Center', distance: '300m', isOpen: true, trustedBy: 89, reputationRequired: 500, hasCharging: false }
    ],
    fareHistory: [
      { time: '6AM', price: 400 },
      { time: '9AM', price: 450 },
      { time: '12PM', price: 400 },
      { time: '3PM', price: 500 },
      { time: '5PM', price: 600 },
      { time: '7PM', price: 600 },
    ],
    sevenDayHistory: [
      { date: 'Mon', avgPrice: 400 },
      { date: 'Tue', avgPrice: 420 },
      { date: 'Wed', avgPrice: 450 },
      { date: 'Thu', avgPrice: 400 },
      { date: 'Fri', avgPrice: 600 },
      { date: 'Sat', avgPrice: 350 },
      { date: 'Sun', avgPrice: 300 },
    ]
  },
  {
    id: 'bs2',
    type: 'bus',
    from: 'CMS',
    to: 'Ajah',
    busStop: 'CMS/Marina',
    location: 'Lagos Island',
    state: 'Lagos',
    country: 'Nigeria',
    crowdLevel: 'high',
    busAvailability: 'frequent',
    securityStatus: 'official-presence',
    lastUpdated: '10 mins ago',
    reporter: 'Bisi O.',
    reporterScore: 2100,
    upvotes: 42,
    slangTag: 'BRT is moving',
    verifications: 12,
    isVerifiedByUser: false,
    estimatedFare: '₦400',
    currentFareValue: 400,
    averageFareValue: 400,
    isFareConfirmed: false,
    isComplained: false,
    isSharedRideAvailable: false,
    typicalDuration: '30-45 mins',
    trafficDensity: 'moving',
    chatCount: 5,
    conductorSafetyRating: 4.8,
    conductorReviewCount: 22,
    lastLocationLandmark: 'Lekki Phase 1',
    lastLocationTime: '5 mins ago',
    isSOSActive: false,
    activeBuddyRequests: 5,
    safeHouses: [
      { id: 'sh3', name: 'EcoBank Marina', type: 'Bank/Security', distance: '50m', isOpen: true, trustedBy: 245, reputationRequired: 1000, hasCharging: true },
      { id: 'sh4', name: 'Mama G Kitchen', type: 'Restaurant', distance: '180m', isOpen: false, trustedBy: 67, reputationRequired: 0, hasCharging: true }
    ],
    fareHistory: [
      { time: '6AM', price: 300 },
      { time: '9AM', price: 400 },
      { time: '12PM', price: 350 },
      { time: '3PM', price: 400 },
      { time: '5PM', price: 400 },
      { time: '7PM', price: 400 },
    ],
    sevenDayHistory: [
      { date: 'Mon', avgPrice: 400 },
      { date: 'Tue', avgPrice: 400 },
      { date: 'Wed', avgPrice: 400 },
      { date: 'Thu', avgPrice: 400 },
      { date: 'Fri', avgPrice: 400 },
      { date: 'Sat', avgPrice: 400 },
      { date: 'Sun', avgPrice: 400 },
    ]
  },
  {
    id: 'tr1',
    type: 'train',
    from: 'Marina',
    to: 'Mile 2',
    busStop: 'Lagos Blue Line (Marina)',
    location: 'Marina Waterfront',
    state: 'Lagos',
    country: 'Nigeria',
    crowdLevel: 'medium',
    busAvailability: 'frequent',
    securityStatus: 'official-presence',
    lastUpdated: '5 mins ago',
    reporter: 'Tunde W.',
    reporterScore: 3200,
    upvotes: 156,
    slangTag: 'Blue Line on point',
    verifications: 45,
    isVerifiedByUser: false,
    estimatedFare: '₦750',
    currentFareValue: 750,
    averageFareValue: 750,
    isFareConfirmed: true,
    isComplained: false,
    isSharedRideAvailable: false,
    typicalDuration: '27 mins',
    trafficDensity: 'clear',
    chatCount: 8,
    conductorSafetyRating: 4.9,
    conductorReviewCount: 120,
    lastLocationLandmark: 'National Theatre',
    lastLocationTime: '4 mins ago',
    isSOSActive: false,
    activeBuddyRequests: 2,
    lastTrainTime: '22:45', // Use a time that would trigger a warning soon
    coachCrowding: [
      { coach: 1, level: 'high', type: 'family' },
      { coach: 2, level: 'full', type: 'general' },
      { coach: 3, level: 'medium', type: 'quiet' },
      { coach: 4, level: 'low', type: 'general' },
    ],
    platformSafetyZones: [
      { id: 'pz1', name: 'Marina Main Security Post', type: 'police' },
      { id: 'pz2', name: 'Emergency Call Box (Platform 1)', type: 'emergency-phone' },
      { id: 'pz3', name: 'Ticket Hall Staff Desk', type: 'staff' },
    ],
    safeHouses: [
      { id: 'sh7', name: 'LRT Security Office', type: 'Official Security', distance: '10m', isOpen: true, trustedBy: 1200, reputationRequired: 0, hasCharging: true }
    ],
    fareHistory: [
      { time: '6AM', price: 750 },
      { time: '12PM', price: 750 },
      { time: '6PM', price: 750 },
    ],
    sevenDayHistory: [
      { date: 'Mon', avgPrice: 750 },
      { date: 'Tue', avgPrice: 750 },
      { date: 'Wed', avgPrice: 750 },
      { date: 'Thu', avgPrice: 750 },
      { date: 'Fri', avgPrice: 750 },
      { date: 'Sat', avgPrice: 750 },
      { date: 'Sun', avgPrice: 750 },
    ]
  },
  {
    id: 'bs3',
    type: 'bus',
    from: 'Wuse Zone 4',
    to: 'Gwarinpa',
    busStop: 'Wuse Zone 4',
    location: 'Abuja Municipal',
    state: 'Abuja',
    country: 'Nigeria',
    crowdLevel: 'low',
    busAvailability: 'frequent',
    securityStatus: 'safe',
    lastUpdated: '15 mins ago',
    reporter: 'Musa Y.',
    reporterScore: 980,
    upvotes: 12,
    slangTag: 'Clear Road',
    verifications: 5,
    isVerifiedByUser: false,
    estimatedFare: '₦300',
    currentFareValue: 300,
    averageFareValue: 300,
    isFareConfirmed: false,
    isComplained: false,
    isSharedRideAvailable: true,
    typicalDuration: '20-30 mins',
    trafficDensity: 'clear',
    chatCount: 3,
    conductorSafetyRating: 3.5,
    conductorReviewCount: 8,
    lastLocationLandmark: 'Banex',
    lastLocationTime: '8 mins ago',
    isSOSActive: false,
    activeBuddyRequests: 1,
    safeHouses: [
      { id: 'sh5', name: 'NNPC Mega Station', type: 'Fuel Station/Lights', distance: '250m', isOpen: true, trustedBy: 112, reputationRequired: 0, hasCharging: true }
    ],
    fareHistory: [
      { time: '6AM', price: 200 },
      { time: '9AM', price: 300 },
      { time: '12PM', price: 250 },
      { time: '3PM', price: 300 },
      { time: '5PM', price: 300 },
      { time: '7PM', price: 300 },
    ],
    sevenDayHistory: [
      { date: 'Mon', avgPrice: 300 },
      { date: 'Tue', avgPrice: 300 },
      { date: 'Wed', avgPrice: 300 },
      { date: 'Thu', avgPrice: 300 },
      { date: 'Fri', avgPrice: 300 },
      { date: 'Sat', avgPrice: 200 },
      { date: 'Sun', avgPrice: 200 },
    ]
  },
  {
    id: 'bs4',
    type: 'bus',
    from: 'Ojota',
    to: 'Ikorodu',
    busStop: 'Ojota Motor Park',
    location: 'Ikorodu Road',
    state: 'Lagos',
    country: 'Nigeria',
    crowdLevel: 'high',
    busAvailability: 'none',
    securityStatus: 'alert',
    lastUpdated: '5 mins ago',
    reporter: 'Nkechi J.',
    reporterScore: 1650,
    upvotes: 124,
    slangTag: 'No Bus!',
    verifications: 38,
    isVerifiedByUser: false,
    estimatedFare: '₦1200',
    currentFareValue: 1200,
    averageFareValue: 800,
    isFareConfirmed: false,
    isComplained: false,
    isSharedRideAvailable: true,
    typicalDuration: '60-90 mins',
    trafficDensity: 'gridlock',
    chatCount: 28,
    conductorSafetyRating: 2.1,
    conductorReviewCount: 45,
    lastLocationLandmark: 'Ket_u',
    lastLocationTime: '12 mins ago',
    isSOSActive: true,
    activeBuddyRequests: 8,
    safeHouses: [
      { id: 'sh6', name: 'Ojota Police Post', type: 'Security Outpost', distance: '40m', isOpen: true, trustedBy: 530, reputationRequired: 0, hasCharging: false }
    ],
    fareHistory: [
      { time: '6AM', price: 800 },
      { time: '9AM', price: 1000 },
      { time: '12PM', price: 800 },
      { time: '3PM', price: 900 },
      { time: '5PM', price: 1200 },
      { time: '7PM', price: 1200 },
    ],
    sevenDayHistory: [
      { date: 'Mon', avgPrice: 800 },
      { date: 'Tue', avgPrice: 850 },
      { date: 'Wed', avgPrice: 800 },
      { date: 'Thu', avgPrice: 1000 },
      { date: 'Fri', avgPrice: 1200 },
      { date: 'Sat', avgPrice: 700 },
      { date: 'Sun', avgPrice: 700 },
    ]
  }
];

export function BusStopCrowdWatch() {
  const [reports, setReports] = React.useState<BusStopReport[]>(DUMMY_BUS_STOPS);
  const [favorites, setFavorites] = React.useState<string[]>([]);
  const [showTrendId, setShowTrendId] = React.useState<string | null>(null);
  const [showSevenDayId, setShowSevenDayId] = React.useState<string | null>(null);
  const [showNegotiatorId, setShowNegotiatorId] = React.useState<string | null>(null);
  const [showSafeHousesId, setShowSafeHousesId] = React.useState<string | null>(null);
  const [activeChatId, setActiveChatId] = React.useState<string | null>(null);
  const [safeWalkTimer, setSafeWalkTimer] = React.useState<{ id: string; seconds: number; extensions: number } | null>(null);
  const [isGhostModeEnabled, setIsGhostModeEnabled] = React.useState(true);
  const [isQuietHoursActive, setIsQuietHoursActive] = React.useState(false);
  const [batteryStatus, setBatteryStatus] = React.useState<{ level: number; isCritical: boolean }>({ level: 85, isCritical: false });
  const [isLateNightMode, setIsLateNightMode] = React.useState(false);

  React.useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      // Late night is between 11 PM and 5 AM
      const hours = now.getHours();
      const isNight = hours >= 23 || hours < 5;
      
      setIsLateNightMode(prev => {
        if (!prev && isNight) {
          toast.info('LATE NIGHT TRANSIT MODE', {
            description: 'UI dimmed for comfort. SOS button prominence boosted for safety.',
            icon: <Moon className="w-4 h-4 text-indigo-400" />
          });
        }
        return isNight;
      });
    };

    checkTime();
    const interval = setInterval(checkTime, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  const [lastKnownSignal, setLastKnownSignal] = React.useState<{ id: string; location: string; time: string } | null>(null);
  const [isOfflineSnapshotReady, setIsOfflineSnapshotReady] = React.useState(false);
  const userReputation = 750; // Mock user reputation

  // Check for Quiet Hours (Auto-Ghost Mode)
  React.useEffect(() => {
    const checkQuietHours = () => {
      const hour = new Date().getHours();
      const isActive = hour >= 22 || hour < 6; // 10 PM to 6 AM
      setIsQuietHoursActive(isActive);
      if (isActive && !isGhostModeEnabled) {
        setIsGhostModeEnabled(true);
        toast.info('QUIET HOURS ACTIVE', {
          description: 'Ghost Mode automatically enabled for nighttime safety.',
          icon: <Moon className="w-4 h-4 text-indigo-500" />
        });
      }
    };

    const interval = setInterval(checkQuietHours, 60000); // Check every minute
    checkQuietHours();
    return () => clearInterval(interval);
  }, [isGhostModeEnabled]);

  // Simulate Battery/Signal Status for Last Known Signal
  React.useEffect(() => {
    const simulateBattery = () => {
      setBatteryStatus(prev => {
        const nextLevel = Math.max(0, prev.level - 1);
        const isCritical = nextLevel <= 15;
        if (isCritical && !prev.isCritical) {
          setIsOfflineSnapshotReady(true);
          toast.warning('CRITICAL BATTERY', {
            description: 'Battery below 15%. "Last Known Signal" and Offline Route Snapshot pinned.',
            icon: <BatteryLow className="w-5 h-5 text-red-500" />
          });
        }
        if (nextLevel === 10) {
          toast.error('EMERGENCY POWER MODE', {
            description: 'Battery at 10%. UI dimmed to save power. Find a Charging Safe House!',
            icon: <ZapOff className="w-5 h-5 text-red-600" />,
            duration: 8000
          });
        }
        return { level: nextLevel, isCritical };
      });
    };

    const interval = setInterval(simulateBattery, 30000); // Simulate drain
    return () => clearInterval(interval);
  }, []);

  const getNegotiatorAdvice = (current?: number, average?: number) => {
    if (!current || !average) return "Standard market rates apply. Pay the posted fare.";
    const diff = current - average;
    if (diff <= 0) return "Great price! This is at or below the usual fare. Pay immediately.";
    if (diff <= 100) return `Slight hike detected. Try to negotiate down to ₦${average + 50} or ₦${average}.`;
    if (diff <= 300) return `Heavy hike! Conductor is charging ₦${diff} extra. Stand your ground at ₦${average + 100}.`;
    return `EXTREME HIKE! Total rip-off. Only pay ₦${average + 200} max or wait for another bus.`;
  };

  const handleRateConductor = (id: string) => {
    toast.success('Rating submitted!', {
      description: 'Your safety feedback helps the entire community stay alert.',
      icon: <Star className="w-4 h-4 text-yellow-500 fill-current" />
    });
  };

  const handleSOS = (id: string) => {
    setReports(prev => prev.map(r => r.id === id ? { ...r, isSOSActive: true } : r));
    toast.error('EMERGENCY SOS ACTIVATED!', {
      description: 'Alert sent to all neighbors on this route. Emergency services notified.',
      duration: 10000,
      icon: <AlertTriangle className="w-6 h-6 animate-pulse" />
    });
  };

  const handleToggleBuddy = (id: string) => {
    setReports(prev => prev.map(r => {
      if (r.id === id) {
        const isNowLooking = !r.isUserLookingForBuddy;
        if (isNowLooking) {
          const othersLooking = r.activeBuddyRequests || 0;
          if (batteryStatus.level <= 15) {
            toast.success('POWER SHARING BUDDY SEARCH', {
              description: 'Matching you with neighbors with >80% battery for shared commute support.',
              icon: <Zap className="w-5 h-5 text-amber-500 animate-pulse" />
            });
          } else if (othersLooking > 0) {
            toast.success('BUDDY MATCH FOUND!', {
              description: `You have been paired with a neighbor (Reputation: 1,200+). Meet at the ${r.busStop} pillar.`,
              duration: 6000,
              icon: <CheckCircle2 className="w-5 h-5 text-green-500" />
            });
          } else {
            toast.success('Buddy Search Active!', {
              description: 'We are matching you with other verified neighbors on this route.',
              icon: <UsersRound className="w-4 h-4 text-green-500" />
            });
          }
        }
        return { 
          ...r, 
          isUserLookingForBuddy: isNowLooking,
          activeBuddyRequests: isNowLooking ? (r.activeBuddyRequests || 0) + 1 : (r.activeBuddyRequests || 1) - 1
        };
      }
      return r;
    }));
  };

  const handleStartSafeWalk = (id: string) => {
    const stop = reports.find(r => r.id === id);
    setSafeWalkTimer({ id, seconds: 600, extensions: 0 }); // 10 minutes
    setLastKnownSignal({ id, location: stop?.busStop || 'Unknown', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) });
    setReports(prev => prev.map(r => r.id === id ? { ...r, isSafeWalkActive: true } : r));
    toast.info('SAFE WALK TIMER STARTED', {
      description: 'You have 10 minutes to confirm arrival. SOS will trigger automatically if you don\'t.',
      duration: 5000,
      icon: <Timer className="w-5 h-5 text-blue-500" />
    });
  };

  const handleExtendSafeWalk = (id: string) => {
    setSafeWalkTimer(prev => {
      if (!prev || prev.id !== id) return prev;
      if (prev.extensions >= 2) {
        toast.warning('MAX EXTENSIONS REACHED', {
          description: 'For your safety, no further extensions allowed. Please confirm arrival or seek a Safe House.'
        });
        return prev;
      }
      toast.info('SAFE WALK EXTENDED', {
        description: '+5 minutes added to your arrival timer. Neighbors notified of delay.',
        icon: <Plus className="w-5 h-5 text-blue-500" />
      });
      return { ...prev, seconds: prev.seconds + 300, extensions: prev.extensions + 1 };
    });
  };

  const handleConfirmArrival = (id: string) => {
    setSafeWalkTimer(null);
    setReports(prev => prev.map(r => r.id === id ? { ...r, isSafeWalkActive: false } : r));
    toast.success('ARRIVAL CONFIRMED', {
      description: 'Glad you reached home safely! +10 Neighbor Trust Points earned.',
      icon: <CheckCircle2 className="w-5 h-5 text-green-500" />
    });
  };

  React.useEffect(() => {
    if (!safeWalkTimer) return;

    const interval = setInterval(() => {
      setSafeWalkTimer(prev => {
        if (!prev) return null;
        if (prev.seconds <= 1) {
          handleSOS(prev.id);
          return null;
        }
        return { ...prev, seconds: prev.seconds - 1 };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [safeWalkTimer]);

  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<'all' | 'crowded' | 'scarce' | 'favorites' | 'charging' | 'train'>('all');
  const [isAddingNew, setIsAddingNew] = React.useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(true);
  const [mutedUntil, setMutedUntil] = React.useState<number | null>(null);
  const [activeAlerts, setActiveAlerts] = React.useState<string[]>([]);

  React.useEffect(() => {
    // Check if muted
    if (mutedUntil && Date.now() < mutedUntil) return;
    if (mutedUntil && Date.now() >= mutedUntil) setMutedUntil(null);

    // Check for gridlock in favorited routes
    const gridlockFavorites = reports.filter(r => 
      favorites.includes(r.id) && r.trafficDensity === 'gridlock'
    ).map(r => r.id);
    
    setActiveAlerts(gridlockFavorites);

    if (notificationsEnabled && gridlockFavorites.length > 0) {
      const alertedRoute = reports.find(r => r.id === gridlockFavorites[0]);
      if (alertedRoute) {
        toast.error(`TRAFFIC ALERT: ${alertedRoute.from} to ${alertedRoute.to} is in GRIDLOCK!`, {
          description: 'Avoid this route if possible. Seek alternative transport.',
          duration: 5000,
          icon: <AlertOctagon className="w-5 h-5" />
        });
      }
    }
  }, [reports, favorites, notificationsEnabled, mutedUntil]);

  const handleMuteAlerts = () => {
    const oneHourFromNow = Date.now() + 60 * 60 * 1000;
    setMutedUntil(oneHourFromNow);
    toast.info('Alerts muted for 1 hour', {
      description: 'We will resume notifications automatically later.',
      icon: <Clock className="w-4 h-4" />
    });
  };

  const [newStopData, setNewStopData] = React.useState({
    from: '',
    to: '',
    busStop: '',
    location: '',
    crowdLevel: 'medium' as any,
    availability: 'frequent' as any,
    type: 'bus' as 'bus' | 'train',
    fare: '',
    sharedRide: false,
    duration: '',
    traffic: 'moving' as any
  });

  const handleAddNewStop = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStopData.from || !newStopData.to || !newStopData.busStop) {
      toast.error('Please fill in required fields');
      return;
    }

    const newReport: BusStopReport = {
      id: `bs-${Date.now()}`,
      type: newStopData.type,
      from: newStopData.from,
      to: newStopData.to,
      busStop: newStopData.busStop,
      location: newStopData.location || `${newStopData.from} Area`,
      state: 'Lagos',
      country: 'Nigeria',
      crowdLevel: newStopData.crowdLevel,
      busAvailability: newStopData.availability,
      securityStatus: 'safe',
      lastUpdated: 'Just now',
      reporter: 'You',
      reporterScore: 10,
      upvotes: 0,
      slangTag: newStopData.type === 'train' ? 'New Rail Route' : 'New Route',
      verifications: 0,
      isVerifiedByUser: false,
      estimatedFare: newStopData.fare ? `₦${newStopData.fare}` : 'Unknown',
      currentFareValue: parseInt(newStopData.fare) || 0,
      averageFareValue: parseInt(newStopData.fare) || 0,
      isFareConfirmed: false,
      isComplained: false,
      isSharedRideAvailable: newStopData.sharedRide,
      typicalDuration: newStopData.duration || 'Unknown',
      trafficDensity: newStopData.traffic,
      fareHistory: [
        { time: 'Now', price: parseInt(newStopData.fare) || 0 }
      ],
      coachCrowding: newStopData.type === 'train' ? [
        { coach: 1, level: 'low', type: 'general' },
        { coach: 2, level: 'low', type: 'general' }
      ] : undefined
    };

    setReports([newReport, ...reports]);
    setIsAddingNew(false);
    setNewStopData({ from: '', to: '', busStop: '', location: '', crowdLevel: 'medium', availability: 'frequent', type: 'bus', fare: '', sharedRide: false, duration: '', traffic: 'moving' });
    toast.success(`${newStopData.type === 'train' ? 'Train' : 'Bus'} route added to the watch list!`);
  };

  const toggleFavorite = (id: string) => {
    setFavorites(prev => {
      const isFav = prev.includes(id);
      if (isFav) {
        toast.info('Removed from favorites');
        return prev.filter(fid => fid !== id);
      } else {
        toast.success('Added to your favorite bus stops!');
        return [...prev, id];
      }
    });
  };

  const handleVerify = (id: string) => {
    setReports(prev => prev.map(r => {
      if (r.id === id) {
        if (r.isVerifiedByUser) return r;
        toast.success('Thanks for verifying!', {
          description: '+5 Sentlo Coins earned for community help.'
        });
        return { 
          ...r, 
          verifications: (r.verifications || 0) + 1,
          isVerifiedByUser: true,
          reporterScore: r.reporterScore + 5
        };
      }
      return r;
    }));
  };

  const handleConfirmFare = (id: string) => {
    setReports(prev => prev.map(r => {
      if (r.id === id) {
        if (r.isFareConfirmed) return r;
        toast.success('Fare confirmed!', {
          description: 'Your confirmation helps neighbors with their budget.',
          icon: <Zap className="w-4 h-4 text-yellow-500" />
        });
        return { 
          ...r, 
          isFareConfirmed: true,
          reporterScore: r.reporterScore + 2
        };
      }
      return r;
    }));
  };

  const handleComplain = (id: string) => {
    setReports(prev => prev.map(r => {
      if (r.id === id) {
        if (r.isComplained) return r;
        toast.warning('Complaint filed!', {
          description: 'This conductor has been added to the community blacklist report.',
          icon: <UserX className="w-4 h-4 text-red-500" />
        });
        return { 
          ...r, 
          isComplained: true,
          reporterScore: r.reporterScore + 1
        };
      }
      return r;
    }));
  };

  const filteredReports = reports.filter(r => {
    const matchesSearch = r.busStop?.toLowerCase().includes(search.toLowerCase()) || 
                          r.location?.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || 
                         (filter === 'crowded' && (r.crowdLevel === 'high' || r.crowdLevel === 'overcrowded')) ||
                         (filter === 'scarce' && r.busAvailability === 'scarce') ||
                         (filter === 'favorites' && favorites.includes(r.id)) ||
                         (filter === 'train' && r.type === 'train') ||
                         (filter === 'charging' && r.safeHouses?.some(h => h.hasCharging && h.isOpen));
    return matchesSearch && matchesFilter;
  });

  const getCrowdColor = (level: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-50 border-green-100';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-100';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-100';
      case 'overcrowded': return 'text-red-600 bg-red-50 border-red-100 animate-pulse';
      default: return 'text-gray-600 bg-gray-50 border-gray-100';
    }
  };

  const getAvailabilityIcon = (status: string) => {
    switch (status) {
      case 'frequent': return <div className="w-1.5 h-1.5 rounded-full bg-green-500" />;
      case 'scarce': return <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />;
      case 'none': return <div className="w-1.5 h-1.5 rounded-full bg-red-500" />;
      default: return null;
    }
  };

  const getTrafficDetails = (density: string) => {
    switch (density) {
      case 'clear': return { color: 'bg-green-500', label: 'Clear', width: '25%' };
      case 'moving': return { color: 'bg-blue-500', label: 'Moving', width: '50%' };
      case 'heavy': return { color: 'bg-orange-500', label: 'Heavy', width: '75%' };
      case 'gridlock': return { color: 'bg-red-600', label: 'Gridlock', width: '100%' };
      default: return { color: 'bg-gray-300', label: 'Unknown', width: '0%' };
    }
  };

  const calculateLastTrainWarning = (lastTimeStr?: string) => {
    if (!lastTimeStr) return null;
    const now = new Date();
    // Use the today's date from the prompt: Thursday, February 19, 2026
    const [hours, minutes] = lastTimeStr.split(':').map(Number);
    const lastTime = new Date(2026, 1, 19, hours, minutes);

    const diffMs = lastTime.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 0) return { label: 'Missed', color: 'bg-red-600', isCritical: true };
    if (diffMins <= 60) return { label: `${diffMins}m left`, color: 'bg-orange-600', isCritical: true };
    if (diffMins <= 120) return { label: `${Math.floor(diffMins/60)}h ${diffMins%60}m left`, color: 'bg-amber-500', isCritical: false };
    return { label: lastTimeStr, color: 'bg-gray-500', isCritical: false };
  };

  const handleUpdate = (id: string) => {
    toast.success('Crowd status updated!', {
      description: 'Your contribution helps neighbors plan their commute.',
      icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
    });
  };

  const joinRouteChat = (id: string) => {
    const route = reports.find(r => r.id === id);
    if (!route) return;
    
    setActiveChatId(id);
    toast.success(`Joined ${route.from} → ${route.to} Chat!`, {
      description: 'You can now coordinate carpooling or shared rides with neighbors.',
      icon: <UsersRound className="w-4 h-4 text-blue-500" />
    });
  };

  return (
    <div className={`flex flex-col gap-6 p-6 md:p-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl border-2 border-purple-200 shadow-2xl shadow-purple-500/20 relative overflow-hidden transition-all duration-1000 ${batteryStatus.level <= 10 ? 'brightness-[0.7] grayscale-[0.3]' : isLateNightMode ? 'brightness-[0.85] contrast-[1.05]' : ''}`}>
      {/* Animated background blobs */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 left-0 w-56 h-56 bg-gradient-to-tr from-pink-400/20 to-orange-400/20 rounded-full blur-3xl animate-pulse delay-1000" />
      
      {batteryStatus.level <= 10 && (
        <div className="absolute inset-0 bg-blue-900/10 pointer-events-none z-[100] animate-pulse" />
      )}
      {isLateNightMode && (
        <div className="absolute inset-0 bg-indigo-950/5 pointer-events-none z-[100]" />
      )}
      {/* Group Chat Overlay */}
      <AnimatePresence>
        {activeChatId && (
          <motion.div 
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="absolute inset-0 z-50 bg-white flex flex-col"
          >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-blue-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <UsersRound className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-black text-sm uppercase tracking-tight">
                    {reports.find(r => r.id === activeChatId)?.from} → {reports.find(r => r.id === activeChatId)?.to}
                  </h3>
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-widest">Shared Commute Chat</p>
                </div>
              </div>
              <button onClick={() => setActiveChatId(null)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-50/50">
              <div className="flex justify-center">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Today</span>
              </div>
              <div className="flex flex-col gap-1 max-w-[80%]">
                <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest ml-2">Segun A.</p>
                <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm">
                  <p className="text-sm text-gray-700">Abeg who is heading to Oshodi now? I'm at Berger bus stop.</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 max-w-[80%] self-end">
                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mr-2 text-right">Bisi O.</p>
                <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-white shadow-md">
                  <p className="text-sm">I'm here o! Wearing a yellow shirt. I have a shared ride (Soole) waiting for 2 more people.</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 max-w-[80%]">
                <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest ml-2">Neighbor</p>
                <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm">
                  <p className="text-sm text-gray-700">How much is the fare now?</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 bg-white">
              <div className="flex gap-2">
                <input 
                  type="text" 
                  placeholder="Ask for shared rides or coordinate..." 
                  className="flex-1 bg-gray-50 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                />
                <button className="p-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-200 active:scale-95 transition-all">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col gap-2 relative z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black flex items-center gap-3 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent uppercase tracking-tight drop-shadow-lg">
            <div className="flex -space-x-1 p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
              <BusFront className="w-6 h-6 text-white drop-shadow-md" />
              <TrainFront className="w-6 h-6 text-white drop-shadow-md" />
            </div>
            Sentlo Crowd Watch
          </h2>
          <div className="flex items-center gap-2">
            {isLateNightMode && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-indigo-900 text-white rounded-lg border border-indigo-700 text-[10px] font-black uppercase tracking-tighter animate-pulse shadow-[0_0_10px_rgba(49,46,129,0.3)]">
                <Moon className="w-3 h-3 fill-current" />
                NIGHT MODE
              </div>
            )}
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-tighter ${batteryStatus.level <= 10 ? 'bg-red-600 text-white border-red-700 animate-[pulse_0.5s_ease-in-out_infinite]' : batteryStatus.isCritical ? 'bg-red-50 text-red-600 border-red-100 animate-pulse' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>
              {batteryStatus.level <= 10 ? <BatteryLow className="w-3 h-3 fill-current" /> : <Battery className="w-3 h-3" />}
              {batteryStatus.level}%
            </div>
            <button 
              onClick={() => {
                setIsGhostModeEnabled(!isGhostModeEnabled);
                toast.info(isGhostModeEnabled ? 'Ghost Mode Disabled' : 'Ghost Mode Enabled', {
                  description: !isGhostModeEnabled ? 'Your location is hidden from neighbors unless you SOS.' : 'Neighbors can see your proximity for better buddy matching.'
                });
              }}
              className={`p-2 rounded-lg transition-all relative group ${isGhostModeEnabled ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
              title={isGhostModeEnabled ? 'Disable Ghost Mode' : 'Enable Ghost Mode'}
            >
              {isGhostModeEnabled ? (
                <div className="relative">
                  <UserX className="w-4 h-4" />
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-indigo-500 rounded-full border border-white" />
                </div>
              ) : (
                <Users className="w-4 h-4" />
              )}
              {isQuietHoursActive && (
                <div className="absolute -bottom-1 -right-1 bg-indigo-600 text-[6px] text-white p-0.5 rounded-full border border-white">
                  <Moon className="w-1.5 h-1.5" />
                </div>
              )}
            </button>
            <button 
              onClick={() => {
                setNotificationsEnabled(!notificationsEnabled);
                toast.info(notificationsEnabled ? 'Smart Alerts Disabled' : 'Smart Alerts Enabled', {
                  description: !notificationsEnabled ? 'We will notify you of gridlock on your favorite routes.' : 'You will no longer receive traffic alert notifications.'
                });
              }}
              className={`p-2 rounded-lg transition-all ${notificationsEnabled ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
              title={notificationsEnabled ? 'Disable Gridlock Alerts' : 'Enable Gridlock Alerts'}
            >
              {notificationsEnabled ? (
                <div className="relative">
                  <Zap className="w-4 h-4 fill-current animate-pulse" />
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-red-500 rounded-full border border-white" />
                </div>
              ) : (
                <Zap className="w-4 h-4" />
              )}
            </button>
            <button 
              onClick={() => setIsAddingNew(true)}
              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              title="Add New Route"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-sm font-bold text-purple-700 drop-shadow-sm">🚌 Real-time crowd levels and bus availability from your neighbors 🚂</p>
      </div>

      <AnimatePresence>
        {activeAlerts.length > 0 && notificationsEnabled && (!mutedUntil || Date.now() >= mutedUntil) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="p-4 bg-red-600 text-white rounded-2xl border border-red-700 shadow-xl flex items-center gap-4 animate-bounce-subtle"
          >
            <div className="p-3 bg-red-700 rounded-xl">
              <AlertOctagon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80 mb-0.5">Critical Route Alert</p>
              <h4 className="text-sm font-black uppercase italic tracking-tight">GRIDLOCK DETECTED!</h4>
              <p className="text-[10px] font-bold opacity-90 leading-tight mt-1">
                {activeAlerts.length} of your favorited routes are currently at a complete standstill. Use alternative routes now.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => setFilter('favorites')}
                className="px-3 py-2 bg-white text-red-600 rounded-xl text-[8px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors shadow-sm"
              >
                View Routes
              </button>
              <button 
                onClick={handleMuteAlerts}
                className="px-3 py-2 bg-red-700 text-white border border-red-500 rounded-xl text-[8px] font-black uppercase tracking-widest hover:bg-red-800 transition-colors"
              >
                Mute 1h
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAddingNew && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <form onSubmit={handleAddNewStop} className="p-5 bg-blue-50/50 rounded-2xl border border-blue-100 space-y-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs font-black uppercase tracking-widest text-blue-600">Add a New Route</h3>
                <button type="button" onClick={() => setIsAddingNew(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex gap-2 mb-4">
                <button 
                  type="button"
                  onClick={() => setNewStopData({ ...newStopData, type: 'bus' })}
                  className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border flex items-center justify-center gap-2 ${newStopData.type === 'bus' ? 'bg-blue-600 text-white border-blue-700 shadow-md' : 'bg-white text-gray-500 border-gray-100 hover:bg-gray-50'}`}
                >
                  <BusFront className="w-3 h-3" />
                  Bus
                </button>
                <button 
                  type="button"
                  onClick={() => setNewStopData({ ...newStopData, type: 'train' })}
                  className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border flex items-center justify-center gap-2 ${newStopData.type === 'train' ? 'bg-indigo-600 text-white border-indigo-700 shadow-md' : 'bg-white text-gray-500 border-gray-100 hover:bg-gray-50'}`}
                >
                  <TrainFront className="w-3 h-3" />
                  Train
                </button>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input 
                    required
                    type="text" 
                    placeholder="From (e.g. Ojota)" 
                    className="w-full bg-white border border-blue-100 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    value={newStopData.from}
                    onChange={(e) => setNewStopData({ ...newStopData, from: e.target.value })}
                  />
                  <input 
                    required
                    type="text" 
                    placeholder="To (e.g. Ikorodu)" 
                    className="w-full bg-white border border-blue-100 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    value={newStopData.to}
                    onChange={(e) => setNewStopData({ ...newStopData, to: e.target.value })}
                  />
                </div>
                <input 
                  required
                  type="text" 
                  placeholder="Bus Stop Name (e.g. Ojota Motor Park)" 
                  className="w-full bg-white border border-blue-100 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={newStopData.busStop}
                  onChange={(e) => setNewStopData({ ...newStopData, busStop: e.target.value })}
                />
                <input 
                  type="text" 
                  placeholder="Detailed Location (Optional)" 
                  className="w-full bg-white border border-blue-100 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={newStopData.location}
                  onChange={(e) => setNewStopData({ ...newStopData, location: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">Traffic Density</p>
                    <select 
                      className="w-full bg-white border border-blue-100 rounded-xl py-3 px-3 text-xs font-bold uppercase tracking-widest focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                      value={newStopData.traffic}
                      onChange={(e) => setNewStopData({ ...newStopData, traffic: e.target.value as any })}
                    >
                      <option value="clear">Clear</option>
                      <option value="moving">Moving</option>
                      <option value="heavy">Heavy</option>
                      <option value="gridlock">Gridlock</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">Crowd Level</p>
                    <select 
                      className="w-full bg-white border border-blue-100 rounded-xl py-3 px-3 text-xs font-bold uppercase tracking-widest focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                      value={newStopData.crowdLevel}
                      onChange={(e) => setNewStopData({ ...newStopData, crowdLevel: e.target.value as any })}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="overcrowded">Overcrowded</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₦</div>
                    <input 
                      type="text" 
                      placeholder="Fare" 
                      className="w-full bg-white border border-blue-100 rounded-xl py-3 pl-8 pr-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      value={newStopData.fare}
                      onChange={(e) => setNewStopData({ ...newStopData, fare: e.target.value })}
                    />
                  </div>
                  <input 
                    type="text" 
                    placeholder="Duration (e.g. 30 mins)" 
                    className="w-full bg-white border border-blue-100 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    value={newStopData.duration}
                    onChange={(e) => setNewStopData({ ...newStopData, duration: e.target.value })}
                  />
                </div>
                <label className="flex items-center gap-3 p-3 bg-white border border-blue-100 rounded-xl cursor-pointer hover:bg-blue-50 transition-colors">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                    checked={newStopData.sharedRide}
                    onChange={(e) => setNewStopData({ ...newStopData, sharedRide: e.target.checked })}
                  />
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-widest">Shared Ride / Soole Available</span>
                </label>
                <button 
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg active:scale-[0.98]"
                >
                  Confirm & Add to Watch List
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input 
            type="text"
            placeholder="Search bus stops or areas..."
            className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <button 
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            All Stops
          </button>
          <button 
            onClick={() => setFilter('train')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${filter === 'train' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            <TrainFront className={`w-3 h-3 ${filter === 'train' ? 'fill-current' : ''}`} />
            Trains
          </button>
          <button 
            onClick={() => setFilter('crowded')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${filter === 'crowded' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            Crowded
          </button>
          <button 
            onClick={() => setFilter('scarce')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${filter === 'scarce' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            No Bus
          </button>
          <button 
            onClick={() => setFilter('favorites')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${filter === 'favorites' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            <Star className={`w-3 h-3 ${filter === 'favorites' ? 'fill-current' : ''}`} />
            Favorites
          </button>
          <button 
            onClick={() => setFilter('charging')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${filter === 'charging' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            <Zap className={`w-3 h-3 ${filter === 'charging' ? 'fill-current' : ''}`} />
            Power Bank
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {filteredReports.length > 0 ? (
          filteredReports.map((stop) => (
            <motion.div 
              key={stop.id}
              layout
              className={`p-5 rounded-2xl border transition-all group relative overflow-hidden ${stop.isSOSActive ? 'border-red-500 bg-red-50 shadow-[0_0_20px_rgba(239,68,68,0.2)]' : favorites.includes(stop.id) ? 'border-yellow-200 bg-yellow-50/10' : 'border-gray-100 bg-white hover:border-blue-100'}`}
            >
              {stop.isSOSActive && (
                <div className="absolute top-0 left-0 w-full h-1 bg-red-600 animate-pulse z-20" />
              )}

              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${stop.isSOSActive ? 'text-red-600' : stop.type === 'train' ? 'text-indigo-600' : 'text-blue-600'}`}>
                          {stop.type === 'train' ? 'Origin' : 'From'}
                        </span>
                        <h3 className="font-black text-gray-900 uppercase italic tracking-tight">{stop.from}</h3>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${stop.isSOSActive ? 'text-red-600' : stop.type === 'train' ? 'text-indigo-600' : 'text-blue-600'}`}>
                          {stop.type === 'train' ? 'Destination' : 'To'}
                        </span>
                        <h3 className="font-black text-gray-900 uppercase italic tracking-tight">{stop.to}</h3>
                      </div>
                    </div>
                    {stop.isSOSActive && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white rounded-md text-[8px] font-black uppercase tracking-widest animate-pulse border border-red-700">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        SOS ACTIVE
                      </div>
                    )}
                    {stop.type === 'train' && stop.lastTrainTime && (
                      <div className={`flex items-center gap-1 px-2 py-0.5 ${calculateLastTrainWarning(stop.lastTrainTime)?.color} text-white rounded-md text-[8px] font-black uppercase tracking-widest border border-white/20 shadow-sm`}>
                        <Clock className="w-2.5 h-2.5" />
                        Last: {calculateLastTrainWarning(stop.lastTrainTime)?.label}
                      </div>
                    )}
                    {stop.securityStatus === 'official-presence' && (
                    <div className="p-1 bg-blue-50 rounded-md text-blue-600 self-start" title="Official Presence">
                      <ShieldCheck className="w-3.5 h-3.5" />
                    </div>
                  )}
                  {favorites.includes(stop.id) && (
                    <Star className="w-3.5 h-3.5 text-yellow-500 fill-current self-start" />
                  )}
                  {stop.isSharedRideAvailable && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md text-[8px] font-black uppercase tracking-widest border border-indigo-100 animate-pulse">
                      <Users className="w-2.5 h-2.5" />
                      Shared Ride
                    </div>
                  )}
                  {favorites.includes(stop.id) && stop.trafficDensity === 'gridlock' && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white rounded-md text-[8px] font-black uppercase tracking-widest animate-bounce-subtle">
                      <AlertOctagon className="w-2.5 h-2.5" />
                      CRITICAL ALERT
                    </div>
                  )}
                  {stop.verifications && stop.verifications >= 10 && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 rounded-md text-[8px] font-black uppercase tracking-widest border border-green-200">
                      <Sparkles className="w-2.5 h-2.5 text-green-500 fill-current" />
                      TRUSTED REPORT
                    </div>
                  )}
                  {stop.safeHouses && stop.safeHouses.some(h => h.isOpen) && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md text-[8px] font-black uppercase tracking-widest border border-emerald-100">
                      <ShieldCheck className="w-2.5 h-2.5" />
                      SAFE HOUSES NEARBY
                    </div>
                  )}
                  {stop.safeHouses && stop.safeHouses.some(h => h.hasCharging && h.isOpen) && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-400 text-gray-900 rounded-md text-[8px] font-black uppercase tracking-widest animate-pulse border border-yellow-500 shadow-[0_0_10px_rgba(250,204,21,0.3)]">
                      <Zap className="w-2.5 h-2.5 fill-current" />
                      CHARGING AVAILABLE
                    </div>
                  )}
                  {isGhostModeEnabled && !stop.isSOSActive && !stop.isSafeWalkActive && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-md text-[8px] font-black uppercase tracking-widest border border-gray-200">
                      <UserX className="w-2.5 h-2.5" />
                      GHOST MODE ACTIVE
                    </div>
                  )}
                  {batteryStatus.isCritical && lastKnownSignal?.id === stop.id && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white rounded-md text-[8px] font-black uppercase tracking-widest animate-pulse border border-red-700 w-fit">
                        <SignalLow className="w-2.5 h-2.5" />
                        LAST KNOWN SIGNAL: {lastKnownSignal.time} @ {lastKnownSignal.location}
                      </div>
                      {isOfflineSnapshotReady && (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-indigo-600 text-white rounded-md text-[8px] font-black uppercase tracking-widest border border-indigo-700 w-fit">
                          <HardDrive className="w-2.5 h-2.5" />
                          OFFLINE ROUTE SNAPSHOT READY
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-2">
                  {stop.type === 'train' ? <TrainFront className="w-3 h-3 text-indigo-400" /> : <MapPin className="w-3 h-3 text-gray-400" />}
                  {stop.busStop} • {stop.location}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-[0.1em] border ${getCrowdColor(stop.crowdLevel)}`}>
                  {stop.crowdLevel}
                </div>
                {stop.verifications && stop.verifications > 0 ? (
                  <div className="flex items-center gap-1 text-[8px] font-black text-blue-600 uppercase tracking-widest">
                    <CheckCircle2 className="w-2.5 h-2.5 text-blue-500" />
                    {stop.verifications} Verified
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mb-4 relative z-10">
              <div className="flex justify-between items-center mb-1.5 px-0.5">
                <div className="flex items-center gap-1.5">
                  <Navigation className="w-2.5 h-2.5 text-blue-500" />
                  <p className="text-[8px] font-black text-gray-400 uppercase tracking-[0.15em]">Traffic Density</p>
                </div>
                <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${getTrafficDetails(stop.trafficDensity || '').color} text-white`}>
                  {getTrafficDetails(stop.trafficDensity || '').label}
                </span>
              </div>
              <div className="h-2 w-full bg-gray-100 rounded-lg overflow-hidden flex gap-1">
                {['clear', 'moving', 'heavy', 'gridlock'].map((d, idx) => {
                  const currentDensity = stop.trafficDensity || 'clear';
                  const densities = ['clear', 'moving', 'heavy', 'gridlock'];
                  const currentIndex = densities.indexOf(currentDensity);
                  const isActive = currentIndex >= idx;
                  const colors = {
                    clear: 'bg-green-500',
                    moving: 'bg-blue-500',
                    heavy: 'bg-orange-500',
                    gridlock: 'bg-red-600'
                  };
                  return (
                    <div 
                      key={d} 
                      className={`h-full flex-1 transition-all duration-700 ${isActive ? colors[d as keyof typeof colors] : 'bg-gray-200 opacity-20'}`}
                    />
                  );
                })}
              </div>
            </div>

            {stop.type === 'train' && (
              <div className="mb-4 space-y-3 relative z-10">
                {/* Coach-Specific Crowding */}
                <div className="bg-indigo-50 p-3 rounded-2xl border border-indigo-100">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-1.5">
                      <BarChart3 className="w-3 h-3 text-indigo-600" />
                      <p className="text-[8px] font-black text-indigo-900 uppercase tracking-widest">Coach-Specific Crowding</p>
                    </div>
                    <span className="text-[7px] font-bold text-indigo-400 uppercase">Live from passengers</span>
                  </div>
                  <div className="flex gap-1.5 h-6">
                    {stop.coachCrowding?.map((c) => (
                      <div key={c.coach} className="flex-1 flex flex-col gap-1">
                        <div className="h-full rounded-md bg-indigo-100 relative overflow-hidden group/coach cursor-help">
                          <div 
                            className={`absolute bottom-0 left-0 w-full transition-all duration-1000 ${
                              c.level === 'low' ? 'h-1/4 bg-green-500' :
                              c.level === 'medium' ? 'h-1/2 bg-amber-500' :
                              c.level === 'high' ? 'h-3/4 bg-orange-500' : 'h-full bg-red-600'
                            }`}
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/coach:opacity-100 transition-opacity bg-white/40">
                            <span className="text-[6px] font-black text-indigo-900 uppercase">{c.level}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <span className="text-[6px] font-black text-indigo-300 text-center">C{c.coach}</span>
                          {c.type && (
                            <span className={`text-[4px] font-black uppercase tracking-tighter px-0.5 rounded-sm ${
                              c.type === 'quiet' ? 'bg-blue-100 text-blue-600' :
                              c.type === 'family' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {c.type}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Safe Walk Suggestion for Last Train */}
                {stop.lastTrainTime && calculateLastTrainWarning(stop.lastTrainTime)?.isCritical && (
                  <motion.div 
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-amber-100 p-3 rounded-2xl border border-amber-200 flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-amber-600 rounded-lg text-white">
                        <UserX className="w-3 h-3" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[9px] font-black text-amber-900 uppercase tracking-tight leading-tight">Last Train Soon! Don't walk alone.</p>
                        <p className="text-[7px] font-bold text-amber-700 uppercase">Station is emptying soon. Activate Safe Walk now.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          setSafeWalkTimer({ minutes: 30, seconds: 0 });
                          toast.success('Safe Walk Timer Started!', {
                            description: 'We will monitor your location until you reach home safely.'
                          });
                        }}
                        className="flex-1 bg-amber-600 text-white py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest active:scale-95 transition-all"
                      >
                        Start Safe Walk
                      </button>
                      <button 
                        onClick={() => toast.info('Buddy Matching Active', { description: 'Looking for other commuters on this route...' })}
                        className="flex-1 bg-white text-amber-600 border border-amber-200 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest active:scale-95 transition-all"
                      >
                        Find Buddy
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Platform Safety Zones */}
                <div className="bg-emerald-50 p-3 rounded-2xl border border-emerald-100">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Shield className="w-3 h-3 text-emerald-600" />
                    <p className="text-[8px] font-black text-emerald-900 uppercase tracking-widest">Platform Safety Zones</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stop.platformSafetyZones?.map((z) => (
                      <div key={z.id} className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-lg border border-emerald-100 shadow-sm">
                        {z.type === 'police' && <ShieldCheck className="w-2.5 h-2.5 text-blue-600" />}
                        {z.type === 'emergency-phone' && <Phone className="w-2.5 h-2.5 text-red-600" />}
                        {z.type === 'staff' && <Headset className="w-2.5 h-2.5 text-emerald-600" />}
                        <span className="text-[7px] font-black text-gray-700 uppercase tracking-tight">{z.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-5 gap-2 mb-4 relative z-10">
              <div className="bg-gray-50 p-2 rounded-xl border border-gray-100/50">
                <p className="text-[7px] font-black text-gray-400 uppercase tracking-widest mb-1">
                  {stop.type === 'train' ? 'Train Frequency' : 'Availability'}
                </p>
                <div className="flex items-center gap-1 relative">
                  {getAvailabilityIcon(stop.busAvailability)}
                  <span className="text-[9px] font-black text-gray-900 uppercase truncate">{stop.busAvailability}</span>
                  {stop.activeBuddyRequests && stop.activeBuddyRequests > 0 && (
                    <div className="flex items-center gap-0.5 ml-auto text-[6px] font-black text-green-600 bg-green-50 px-1 rounded-sm border border-green-100">
                      <UsersRound className="w-2 h-2" />
                      {stop.activeBuddyRequests} Buddies
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 p-2 rounded-xl border border-gray-100/50 relative overflow-hidden group/fare">
                <p className="text-[7px] font-black text-gray-400 uppercase tracking-widest mb-1 flex justify-between">
                  Fare
                  <button 
                    onClick={() => setShowNegotiatorId(showNegotiatorId === stop.id ? null : stop.id)}
                    className="text-blue-600 font-bold"
                  >
                    ?
                  </button>
                </p>
                <div className="flex items-center gap-1">
                  <div className={`w-1 h-1 rounded-full ${stop.currentFareValue && stop.averageFareValue && stop.currentFareValue > stop.averageFareValue ? 'bg-red-500 animate-ping' : 'bg-blue-500'}`} />
                  <span className={`text-[9px] font-black uppercase truncate ${stop.currentFareValue && stop.averageFareValue && stop.currentFareValue > stop.averageFareValue ? 'text-red-600' : 'text-gray-900'}`}>
                    {stop.estimatedFare || '₦ ---'}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 p-2 rounded-xl border border-gray-100/50">
                <p className={`text-[7px] font-black text-gray-400 uppercase tracking-widest mb-1 ${stop.type === 'train' ? 'text-indigo-600' : 'text-blue-600'}`}>
                  {stop.type === 'train' ? 'Train Location' : 'Bus Location'}
                </p>
                <div className="flex flex-col">
                  <span className="text-[9px] font-black text-gray-900 uppercase truncate" title={stop.lastLocationLandmark}>
                    {stop.lastLocationLandmark || 'No Data'}
                  </span>
                  <span className={`text-[6px] font-black uppercase ${stop.type === 'train' ? 'text-indigo-400' : 'text-blue-400'}`}>
                    {stop.lastLocationTime || '---'}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 p-2 rounded-xl border border-gray-100/50">
                <p className="text-[7px] font-black text-gray-400 uppercase tracking-widest mb-1">Est. Time</p>
                <div className="flex items-center gap-1">
                  <Timer className="w-2.5 h-2.5 text-indigo-500" />
                  <span className="text-[9px] font-black text-gray-900 uppercase truncate">{stop.typicalDuration || '---'}</span>
                </div>
              </div>
              <div className="bg-gray-50 p-2 rounded-xl border border-gray-100/50">
                <p className="text-[7px] font-black text-gray-400 uppercase tracking-widest mb-1">Updated</p>
                <div className="flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5 text-gray-400" />
                  <span className="text-[9px] font-black text-gray-900 uppercase truncate">{stop.lastUpdated}</span>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {showSafeHousesId === stop.id && stop.safeHouses && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="mb-4 bg-emerald-900 p-4 rounded-xl border border-emerald-800 shadow-xl relative overflow-hidden"
                >
                  <div className="flex justify-between items-center mb-3 relative z-10">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-400" />
                      <h4 className="text-[10px] font-black text-white uppercase tracking-widest">Neighborhood Safe Houses</h4>
                    </div>
                    <button 
                      onClick={() => setShowSafeHousesId(null)}
                      className="text-emerald-400 hover:text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 relative z-10">
                    {stop.safeHouses.map(house => {
                      const isLocked = house.reputationRequired && house.reputationRequired > userReputation;
                      return (
                        <div key={house.id} className={`flex items-center justify-between bg-emerald-800/50 p-2.5 rounded-lg border border-emerald-700/50 ${isLocked ? 'opacity-70 grayscale' : ''}`}>
                          <div className="flex items-start gap-2.5">
                            <div className={`mt-1 w-2 h-2 rounded-full ${house.isOpen ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-red-400'}`} />
                            <div>
                              <p className="text-[11px] font-black text-white uppercase tracking-tight flex items-center gap-1.5">
                                {isLocked ? '******** (LOCKED)' : house.name}
                                {isLocked && <ShieldCheck className="w-2.5 h-2.5 text-yellow-500" />}
                                {!isLocked && house.hasCharging && (
                                  <span className="flex items-center gap-0.5 bg-emerald-500/30 text-emerald-300 text-[6px] px-1 rounded-sm border border-emerald-400/30 animate-pulse">
                                    <Zap className="w-1.5 h-1.5 fill-current" />
                                    CHARGING
                                  </span>
                                )}
                              </p>
                              <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">
                                {house.type} • {isLocked ? '???m' : house.distance}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            {isLocked ? (
                              <div className="flex flex-col items-end">
                                <span className="text-[7px] font-black text-yellow-500 uppercase">Requires {house.reputationRequired} RP</span>
                                <span className="text-[6px] font-black text-emerald-300 uppercase">Your RP: {userReputation}</span>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-1 text-[8px] font-black text-emerald-300 uppercase">
                                  <Users className="w-2.5 h-2.5" />
                                  {house.trustedBy} Verified
                                </div>
                                <button className="mt-1 text-[8px] font-black text-emerald-100 underline uppercase tracking-tighter">Navigate</button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 text-[7px] font-bold text-emerald-400/70 italic text-center">
                    These are community-verified locations where you can wait safely or seek help.
                  </div>
                  <div className="absolute -bottom-4 -right-4 opacity-10">
                    <MapIcon className="w-24 h-24 text-white" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showNegotiatorId === stop.id && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="mb-4 bg-blue-600 p-4 rounded-xl border border-blue-700 shadow-xl relative overflow-hidden"
                >
                  <div className="flex items-start gap-3 relative z-10">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Zap className="w-5 h-5 text-yellow-400 fill-current" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-black text-white uppercase tracking-widest mb-1">Fare Negotiator AI Tip</h4>
                      <p className="text-xs font-bold text-blue-50 leading-tight">
                        {getNegotiatorAdvice(stop.currentFareValue, stop.averageFareValue)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center relative z-10">
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-blue-200 uppercase tracking-widest">Normal Rate</span>
                      <span className="text-sm font-black text-white italic">₦{stop.averageFareValue}</span>
                    </div>
                    <button 
                      onClick={() => setShowNegotiatorId(null)}
                      className="px-3 py-1.5 bg-white text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-widest"
                    >
                      Got it!
                    </button>
                  </div>
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <TrendingUp className="w-16 h-16" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showTrendId === stop.id && stop.fareHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 bg-gray-50 p-4 rounded-xl border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                      <History className="w-3 h-3" />
                      Fare Trends (24h)
                    </h4>
                    <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Avg: ₦{stop.averageFareValue}</span>
                  </div>
                  <div className="h-24 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stop.fareHistory}>
                        <defs>
                          <linearGradient id={`colorPrice-${stop.id}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <Area 
                          type="monotone" 
                          dataKey="price" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill={`url(#colorPrice-${stop.id})`} 
                          animationDuration={1000}
                        />
                        <Tooltip 
                          contentStyle={{ fontSize: '10px', fontWeight: '900', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                          formatter={(value: number) => [`₦${value}`, 'Price']}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showSevenDayId === stop.id && stop.sevenDayHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 bg-indigo-50/50 p-4 rounded-xl border border-indigo-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-1.5">
                      <History className="w-3 h-3 text-indigo-500" />
                      7-Day Price Breakdown
                    </h4>
                    <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">Week View</span>
                  </div>
                  <div className="h-24 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stop.sevenDayHistory}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e7ff" />
                        <XAxis dataKey="date" hide />
                        <YAxis hide domain={['dataMin - 50', 'dataMax + 50']} />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-2 rounded-lg border border-indigo-100 shadow-sm text-[10px] font-black text-indigo-600 uppercase">
                                  {payload[0].payload.date}: ₦{payload[0].value}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Line 
                          type="stepAfter" 
                          dataKey="avgPrice" 
                          stroke="#4f46e5" 
                          strokeWidth={2} 
                          dot={{ r: 3, fill: '#4f46e5' }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-between mt-2">
                    {stop.sevenDayHistory.map((d, i) => (
                      <span key={i} className="text-[8px] font-black text-indigo-300 uppercase tracking-tighter">{d.date}</span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center justify-between relative z-10 pt-4 border-t border-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-[10px] font-black text-blue-600 border border-blue-100">
                  {stop.reporter.substring(0, 2).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <p className="text-[10px] font-black text-gray-900 uppercase italic tracking-tight leading-none">{stop.reporter}</p>
                    <div className="flex items-center gap-0.5 text-[8px] font-black text-green-600 bg-green-50 px-1 rounded">
                      <TrendingUp className="w-2 h-2" />
                      {stop.reporterScore}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5 text-[8px] font-black text-yellow-600 bg-yellow-50 px-1 py-0.5 rounded border border-yellow-100/50">
                      <Star className="w-2.5 h-2.5 fill-current" />
                      {stop.conductorSafetyRating || '0.0'}
                    </div>
                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{stop.conductorReviewCount || 0} reviews</span>
                    {stop.slangTag && (
                      <span className="text-[8px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded italic">#{stop.slangTag.replace(/\s+/g, '')}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex items-center gap-1 group/walk-container">
                  <button 
                    onClick={() => stop.isSafeWalkActive ? handleConfirmArrival(stop.id) : handleStartSafeWalk(stop.id)}
                    className={`p-2 rounded-lg transition-all border active:scale-95 shadow-sm relative overflow-hidden group/walk ${stop.isSafeWalkActive ? 'bg-blue-600 text-white border-blue-700 animate-pulse' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100'}`}
                    title={stop.isSafeWalkActive ? "Confirm Arrival Home" : "Start Safe Walk Timer"}
                  >
                    <Navigation className={`w-3.5 h-3.5 ${stop.isSafeWalkActive ? 'fill-current' : ''}`} />
                    {stop.isSafeWalkActive && safeWalkTimer && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[7px] font-black w-3.5 h-3.5 rounded-full flex items-center justify-center border border-white">
                        !
                      </span>
                    )}
                    {stop.isSafeWalkActive && safeWalkTimer && (
                      <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center pointer-events-none">
                        <div className="h-full bg-white/20 transition-all duration-1000 self-start" style={{ width: `${(safeWalkTimer.seconds / 600) * 100}%` }} />
                      </div>
                    )}
                  </button>
                  {stop.isSafeWalkActive && safeWalkTimer && (
                    <button
                      onClick={() => handleExtendSafeWalk(stop.id)}
                      className="p-2 bg-blue-50 text-blue-600 border border-blue-100 rounded-lg hover:bg-blue-100 transition-all active:scale-90 shadow-sm flex items-center gap-1"
                      title="Extend 5 Minutes"
                    >
                      <Plus className="w-2.5 h-2.5" />
                      <span className="text-[7px] font-black">{Math.floor(safeWalkTimer.seconds / 60)}m</span>
                    </button>
                  )}
                </div>
                <button 
                  onClick={() => handleToggleBuddy(stop.id)}
                  className={`p-2 rounded-lg transition-all border active:scale-95 shadow-sm relative group/buddy ${stop.isUserLookingForBuddy ? 'bg-green-600 text-white border-green-700 animate-pulse' : 'bg-green-50 text-green-600 border-green-100 hover:bg-green-100'}`}
                  title={stop.isUserLookingForBuddy ? "Stop Buddy Search" : "Find a Commute Buddy"}
                >
                  <UsersRound className={`w-3.5 h-3.5 ${stop.isUserLookingForBuddy ? 'fill-current' : ''}`} />
                  {stop.activeBuddyRequests && stop.activeBuddyRequests > 0 && !stop.isUserLookingForBuddy && (
                    <span className="absolute -top-1.5 -right-1.5 bg-green-500 text-white text-[7px] font-black w-3.5 h-3.5 rounded-full flex items-center justify-center border border-white shadow-sm ring-1 ring-green-600/10">
                      {stop.activeBuddyRequests}
                    </span>
                  )}
                </button>
                <button 
                  onClick={() => setShowSafeHousesId(showSafeHousesId === stop.id ? null : stop.id)}
                  className={`p-2 rounded-lg transition-all border active:scale-95 shadow-sm ${showSafeHousesId === stop.id ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100'}`}
                  title="Show Safe Houses Map Overlay"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                </button>
                <button 
                  onClick={() => handleSOS(stop.id)}
                  className={`p-2 rounded-lg transition-all border active:scale-95 shadow-sm ${stop.isSOSActive ? 'bg-red-600 text-white border-red-700 animate-pulse' : isLateNightMode ? 'bg-red-500 text-white border-red-600 shadow-[0_0_15px_rgba(239,68,68,0.5)] scale-110' : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'}`}
                  title="EMERGENCY SOS"
                >
                  <AlertTriangle className={`w-3.5 h-3.5 ${stop.isSOSActive || isLateNightMode ? 'fill-current' : ''}`} />
                </button>
                <button 
                  onClick={() => handleRateConductor(stop.id)}
                  className="p-2 rounded-lg transition-colors border bg-yellow-50 text-yellow-500 border-yellow-200 hover:bg-yellow-100 active:scale-95"
                  title="Rate Conductor Safety"
                >
                  <Star className="w-3.5 h-3.5" />
                </button>
                <button 
                  onClick={() => setShowTrendId(showTrendId === stop.id ? null : stop.id)}
                  className={`p-2 rounded-lg transition-colors border active:scale-95 ${showTrendId === stop.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100'}`}
                  title="View 24h Trends"
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                </button>
                <button 
                  onClick={() => setShowSevenDayId(showSevenDayId === stop.id ? null : stop.id)}
                  className={`p-2 rounded-lg transition-colors border active:scale-95 ${showSevenDayId === stop.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100'}`}
                  title="View 7-Day History"
                >
                  <History className="w-3.5 h-3.5" />
                </button>
                <button 
                  onClick={() => joinRouteChat(stop.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95 border bg-green-50 text-green-600 border-green-200 hover:bg-green-100"
                  title="Join Commute Chat"
                >
                  <UsersRound className="w-3 h-3" />
                  Chat
                  {stop.chatCount && (
                    <span className="bg-red-500 text-white px-1 rounded-full text-[6px] ml-0.5">{stop.chatCount}</span>
                  )}
                </button>
                <button 
                  onClick={() => handleConfirmFare(stop.id)}
                  disabled={stop.isFareConfirmed}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95 border ${stop.isFareConfirmed ? 'bg-yellow-50 text-yellow-600 border-yellow-200 cursor-default' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                  title="Confirm Fare"
                >
                  <Zap className={`w-3 h-3 ${stop.isFareConfirmed ? 'fill-current' : ''}`} />
                  {stop.isFareConfirmed ? 'OK' : 'Confirm'}
                </button>
                <button 
                  onClick={() => toggleFavorite(stop.id)}
                  className={`p-2 rounded-lg transition-colors border active:scale-95 ${favorites.includes(stop.id) ? 'bg-yellow-50 text-yellow-500 border-yellow-200' : 'bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100'}`}
                  title="Save to Favorites"
                >
                  <Star className={`w-4 h-4 ${favorites.includes(stop.id) ? 'fill-current' : ''}`} />
                </button>
              </div>
            </div>

            {/* Background Decoration */}
            <div className="absolute top-0 right-0 p-8 opacity-[0.03] -translate-y-4 translate-x-4">
              {stop.type === 'train' ? (
                <TrainFront className="w-32 h-32 rotate-12" />
              ) : (
                <BusFront className="w-32 h-32 rotate-12" />
              )}
            </div>
          </motion.div>
        ))) : (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-12 px-6 bg-gray-50 rounded-[32px] border-2 border-dashed border-gray-200 text-center"
          >
            <div className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100 mb-4">
              <ZapOff className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight mb-2">No Results Found</h3>
            <p className="text-[10px] font-bold text-gray-500 uppercase leading-relaxed max-w-[200px] mx-auto">
              We couldn't find any {filter === 'charging' ? 'Safe Houses with charging' : filter === 'train' ? 'Train routes' : 'matching routes'} right now.
            </p>
            <button 
              onClick={() => { setFilter('all'); setSearch(''); }}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-200 active:scale-95 transition-all"
            >
              Reset Filters
            </button>
          </motion.div>
        )}
      </div>

      <div className="mt-4 p-5 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[32px] text-white relative overflow-hidden group">
        <div className="relative z-10 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400 fill-current animate-pulse" />
            <h3 className="text-sm font-black italic uppercase tracking-tight">Earn Coins by Reporting</h3>
          </div>
          <p className="text-xs text-white/80 leading-relaxed max-w-[200px]">
            Help neighbors avoid overcrowding. Every verified crowd report earns you <span className="font-black text-white underline decoration-yellow-400 decoration-2">10 Sentlo Coins</span>.
          </p>
          <button className="bg-white text-blue-700 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2 group-hover:bg-blue-50">
            <Plus className="w-3.5 h-3.5" />
            Report Crowd Level
          </button>
        </div>
        <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
          <Sparkles className="w-32 h-32 rotate-12" />
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 text-[9px] font-black text-gray-400 uppercase tracking-widest pt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Live Updates
        </div>
        <div className="w-px h-3 bg-gray-200" />
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-green-600" />
          Community Verified
        </div>
      </div>
    </div>
  );
}
